import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Modal, copyText } from '@ecom/ui-kit';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** Mirrors `NotificationSettingsView` (apps/api/src/modules/delivery/delivery-notification-settings.service.ts). */
interface NotificationSettings {
  readonly available: boolean;
  readonly accountConnected: boolean;
  readonly configured: boolean;
  readonly url: string | null;
  readonly createdAt: string | null;
  readonly rotatedAt: string | null;
}

const PATH = '/merchant/delivery/jibex/notifications';

function useNotificationSettings(): UseQueryResult<NotificationSettings, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['jibex-notifications', activeShopId],
    queryFn: () => apiRequest<NotificationSettings>(PATH, { accessToken, shopId: activeShopId }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

function useNotificationAction<T>(path: string): UseMutationResult<T, ApiError, void> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<T>(path, { method: 'POST', accessToken, shopId: activeShopId }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['jibex-notifications', activeShopId] }),
  });
}

/**
 * « Notifications Jibex »: the address and the signing secret the owner hands to the delivering company, so status
 * changes arrive at once (the periodic follow-up stays as a safety net).
 *
 * The secret is shown ONCE, right after it is generated, and kept in this component's memory only — never in a query
 * cache, never in storage. Closing the dialog forgets it; generating again rotates it (the old one stops working).
 */
export function JibexNotificationsCard(): ReactElement {
  const { t } = useI18n();
  const settings = useNotificationSettings();
  const issue = useNotificationAction<{ url: string; secret: string }>(`${PATH}/secret`);
  const disable = useNotificationAction<NotificationSettings>(`${PATH}/disable`);
  const [revealed, setRevealed] = useState<{ url: string; secret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'secret' | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);

  function generate(): void {
    setError(null);
    issue.mutate(undefined, {
      onSuccess: (result) => {
        setRevealed(result);
      },
      onError: (failure) => {
        setError(t(resolveErrorMessageKey(failure)));
      },
      onSettled: () => {
        // The answer held the secret: the mutation forgets it, only `revealed` keeps it until the dialog closes.
        issue.reset();
      },
    });
  }

  function copy(kind: 'url' | 'secret', text: string): void {
    void copyText(text).then((ok) => {
      if (ok) setCopied(kind);
    });
  }

  const view = settings.data;

  return (
    <section className="rounded-md border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">
        {t('merchant.delivery.notifications.title')}
      </h2>
      <p className="mt-1 text-sm text-inkSoft">
        {t('merchant.delivery.notifications.description')}
      </p>

      {settings.isPending ? (
        <p className="mt-3 text-sm text-inkFaint">{t('merchant.common.loadingAria')}</p>
      ) : settings.isError ? (
        <p role="alert" className="mt-3 text-sm text-critical">
          {t(resolveErrorMessageKey(settings.error))}
        </p>
      ) : view !== undefined && !view.available ? (
        <p className="mt-3 text-sm text-inkSoft">
          {t('merchant.delivery.notifications.unavailable')}
        </p>
      ) : view !== undefined && !view.accountConnected ? (
        <p className="mt-3 text-sm text-inkSoft">
          {t('merchant.delivery.notifications.needAccount')}
        </p>
      ) : view !== undefined ? (
        <div className="mt-3 flex flex-col gap-3 text-sm">
          {view.configured && view.url !== null ? (
            <>
              <p className="text-good">{t('merchant.delivery.notifications.configured')}</p>
              <div className="flex flex-wrap items-center gap-2">
                <code
                  className="break-all rounded bg-surface2 px-2 py-1 font-mono text-xs"
                  dir="ltr"
                >
                  {view.url}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    copy('url', view.url ?? '');
                  }}
                >
                  {copied === 'url'
                    ? t('merchant.ship.detail.copied')
                    : t('merchant.ship.detail.copy')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={issue.isPending}
                  onClick={() => {
                    setConfirmRotate(true);
                  }}
                >
                  {t('merchant.delivery.notifications.rotate')}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={disable.isPending}
                  onClick={() => {
                    setError(null);
                    disable.mutate(undefined, {
                      onError: (failure) => {
                        setError(t(resolveErrorMessageKey(failure)));
                      },
                    });
                  }}
                >
                  {t('merchant.delivery.notifications.disable')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-inkSoft">{t('merchant.delivery.notifications.notConfigured')}</p>
              <div>
                <Button size="sm" loading={issue.isPending} onClick={generate}>
                  {t('merchant.delivery.notifications.generate')}
                </Button>
              </div>
            </>
          )}
          {error !== null ? (
            <p role="alert" className="text-critical">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      <Modal
        open={confirmRotate}
        title={t('merchant.delivery.notifications.rotate')}
        description={t('merchant.delivery.notifications.rotateConfirm')}
        onClose={() => {
          setConfirmRotate(false);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmRotate(false);
              }}
            >
              {t('merchant.ship.cancel.keep')}
            </Button>
            <Button
              onClick={() => {
                setConfirmRotate(false);
                generate();
              }}
            >
              {t('merchant.delivery.notifications.rotate')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-inkSoft">{t('merchant.delivery.notifications.secretOnce')}</p>
      </Modal>

      <Modal
        open={revealed !== null}
        title={t('merchant.delivery.notifications.revealTitle')}
        description={t('merchant.delivery.notifications.secretOnce')}
        onClose={() => {
          setRevealed(null);
          setCopied(null);
        }}
        footer={
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setRevealed(null);
                setCopied(null);
              }}
            >
              {t('merchant.delivery.notifications.done')}
            </Button>
          </div>
        }
      >
        {revealed !== null ? (
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-inkSoft">{t('merchant.delivery.notifications.steps')}</p>
            <div>
              <p className="font-medium text-ink">
                {t('merchant.delivery.notifications.urlLabel')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <code
                  className="break-all rounded bg-surface2 px-2 py-1 font-mono text-xs"
                  dir="ltr"
                >
                  {revealed.url}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    copy('url', revealed.url);
                  }}
                >
                  {copied === 'url'
                    ? t('merchant.ship.detail.copied')
                    : t('merchant.ship.detail.copy')}
                </Button>
              </div>
            </div>
            <div>
              <p className="font-medium text-ink">
                {t('merchant.delivery.notifications.secretLabel')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <code
                  className="break-all rounded bg-surface2 px-2 py-1 font-mono text-xs"
                  dir="ltr"
                >
                  {revealed.secret}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    copy('secret', revealed.secret);
                  }}
                >
                  {copied === 'secret'
                    ? t('merchant.ship.detail.copied')
                    : t('merchant.ship.detail.copy')}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
